import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  useTheme as useMuiTheme,
  alpha,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  formatCurrency,
  formatNumber,
  sxProductionCostOverrideValue,
  sxUnitCostOverrideValue,
} from '../closeoutHelpers';
import { ExtendEstimateWithContext } from './../closeoutTypes';
import { NavigationDirection } from './../TableNavigation';
import {
  useUnitCostUpdater,
  useProductionRateUpdater,
  useOverridesResetter,
  getItemLastUpdatedWarning,
} from './extentionHelper';
import {
  CostDisplayProperties,
  calculateTotalMaterialCost,
  calculateTotalLaborHours,
} from './hooks';

// Import icons
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useLockBid } from '../../../utils/useLockBid';

// Helper function to convert decimal hours to minutes/seconds string
const getMinutesFromDecimal = (decimalHours: string | number): string => {
  const hours = typeof decimalHours === 'string' ? parseFloat(decimalHours) : decimalHours;
  if (isNaN(hours) || decimalHours === '') return '(0s)';

  const totalSeconds = Math.round(hours * 3600); // Convert to seconds
  const wholeHours = Math.floor(totalSeconds / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  // If less than a minute, show seconds
  if (totalSeconds < 60) {
    return `(${totalSeconds}s)`;
  }

  // If less than an hour, show minutes and seconds
  if (wholeHours === 0) {
    return remainingSeconds === 0
      ? `(${remainingMinutes}m)`
      : `(${remainingMinutes}m ${remainingSeconds}s)`;
  }

  // If exact hours, just show hours
  if (remainingMinutes === 0 && remainingSeconds === 0) {
    return `(${wholeHours}h)`;
  }

  // If no seconds, show hours and minutes
  if (remainingSeconds === 0) {
    return `(${wholeHours}h ${remainingMinutes}m)`;
  }

  // Show all units
  return `(${wholeHours}h ${remainingMinutes}m ${remainingSeconds}s)`;
};

export const ExtentionRow: React.FC<{
  item: ExtendEstimateWithContext;
  onOpenCreateItemTab: (itemId: string) => void;
  isSelected?: boolean;
  onSelect?: (itemId: string, selected: boolean, shiftKey?: boolean, columnIndex?: number) => void;
  isBatchEditing?: boolean;
  onNext?: (direction: NavigationDirection) => void;
  isActiveRow?: boolean;
  activeCellIndex?: number | null;
  isShiftSelecting?: boolean;
  selectedColumnIndex?: number | null;
  isEditingDisabled?: boolean;
}> = React.memo(
  ({
    item,
    onOpenCreateItemTab,
    isSelected = false,
    onSelect,
    isBatchEditing = false,
    onNext,
    isActiveRow = false,
    activeCellIndex = null,
    isShiftSelecting = false,
    selectedColumnIndex = null,
    isEditingDisabled = false,
  }) => {
    const muiTheme = useMuiTheme();
    const { darkMode } = useTheme();
    const { lockBid } = useLockBid();

    // Refs for input elements
    const unitCostInputRef = useRef<HTMLInputElement>(null);
    const productionRateInputRef = useRef<HTMLInputElement>(null);

    // Store original values when focusing a field to check if they've changed
    const originalValueRef = useRef<{
      unitCost: string;
      productionRate: string;
    }>({
      unitCost: '',
      productionRate: '',
    });

    // Local state for optimistic updates
    const [localItem, setLocalItem] = useState(() => {
      // Create a copy of the item for local state updates
      return { ...item };
    });

    // Input field states
    const [unitCostInput, setUnitCostInput] = useState<string>(
      item.isoverrideitemunitcost
        ? item.overrideitemunitcost.toFixed(2)
        : item.itemunitcost?.toFixed(2) || '0.00'
    );
    const [productionRateInput, setProductionRateInput] = useState<string>(
      item.isoverridehourlyproductionrate
        ? item.overridehourlyproductionrate.toFixed(3)
        : item.hourlyproductionrate?.toFixed(3) || '0.000'
    );

    // Track if fields are being edited to prevent override during editing
    const [isEditingUnitCost, setIsEditingUnitCost] = useState(false);
    const [isEditingProductionRate, setIsEditingProductionRate] = useState(false);

    // Import and use our custom hooks instead of defining handlers here
    const { commitUnitCostToServer } = useUnitCostUpdater(item, setLocalItem, setUnitCostInput);

    const { commitProductionRateToServer } = useProductionRateUpdater(
      item,
      setLocalItem,
      setProductionRateInput
    );

    const { resetOverrides } = useOverridesResetter(
      item,
      setLocalItem,
      setUnitCostInput,
      setProductionRateInput
    );

    // Get the last updated warning status
    const lastUpdatedWarning = useMemo(() => {
      return getItemLastUpdatedWarning(localItem.itemlastupdated);
    }, [localItem.itemlastupdated]);

    // Last updated warning icon - memoized
    const lastUpdatedWarningIcon = useMemo(() => {
      if (!lastUpdatedWarning) return null;

      return (
        <Tooltip
          title={
            lastUpdatedWarning === 'warning'
              ? 'Cost data is over 45 days old'
              : 'Cost data is over 60 days old - update recommended'
          }
          arrow
        >
          <WarningAmberIcon
            fontSize="small"
            sx={{
              color:
                lastUpdatedWarning === 'warning'
                  ? muiTheme.palette.warning.main
                  : muiTheme.palette.error.main,
              ml: 0.5,
            }}
          />
        </Tooltip>
      );
    }, [lastUpdatedWarning, muiTheme.palette.warning.main, muiTheme.palette.error.main]);

    // Update handleFocus to prevent losing state
    const handleFocus = useCallback(
      (field: 'unitCost' | 'productionRate') => {
        if (field === 'unitCost') {
          setIsEditingUnitCost(true);
          originalValueRef.current.unitCost = unitCostInput;

          // Only commit other field if it's been edited to prevent unnecessary API calls
          if (
            isEditingProductionRate &&
            originalValueRef.current.productionRate !== productionRateInput
          ) {
            setIsEditingProductionRate(false);
            commitProductionRateToServer(productionRateInput);
          } else {
            setIsEditingProductionRate(false);
          }
        } else {
          setIsEditingProductionRate(true);
          originalValueRef.current.productionRate = productionRateInput;

          // Only commit other field if it's been edited to prevent unnecessary API calls
          if (isEditingUnitCost && originalValueRef.current.unitCost !== unitCostInput) {
            setIsEditingUnitCost(false);
            commitUnitCostToServer(unitCostInput);
          } else {
            setIsEditingUnitCost(false);
          }
        }
      },
      [
        unitCostInput,
        productionRateInput,
        isEditingUnitCost,
        isEditingProductionRate,
        commitUnitCostToServer,
        commitProductionRateToServer,
      ]
    );

    // Simplify handleKeyDown to allow native tab behavior
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>, field: 'unitCost' | 'productionRate') => {
        // Don't interfere with Tab navigation
        if (e.key === 'Tab') {
          // Let the browser handle tab navigation naturally
          if (field === 'unitCost') {
            // If we're tabbing away, commit the change
            if (originalValueRef.current.unitCost !== unitCostInput) {
              // We don't prevent default here, allowing tab to work naturally
              // But we do commit our changes before moving focus
              commitUnitCostToServer(unitCostInput);
            }
          } else {
            // Same for production rate
            if (originalValueRef.current.productionRate !== productionRateInput) {
              commitProductionRateToServer(productionRateInput);
            }
          }
          return;
        }

        // Handle Enter and Escape as before
        if (e.key === 'Enter') {
          e.currentTarget.blur(); // Blur the field on Enter to trigger the commit
        } else if (e.key === 'Escape') {
          // Cancel changes
          if (field === 'unitCost') {
            setUnitCostInput(
              formatCurrency(
                item.isoverrideitemunitcost ? item.overrideitemunitcost : item.itemunitcost || 0
              )
            );
          } else {
            setProductionRateInput(
              item.isoverridehourlyproductionrate
                ? item.overridehourlyproductionrate.toFixed(3)
                : item.hourlyproductionrate?.toFixed(3) || '0.000'
            );
          }
          e.currentTarget.blur();
        }
      },
      [
        item.isoverrideitemunitcost,
        item.overrideitemunitcost,
        item.itemunitcost,
        item.isoverridehourlyproductionrate,
        item.overridehourlyproductionrate,
        item.hourlyproductionrate,
        unitCostInput,
        productionRateInput,
        commitUnitCostToServer,
        commitProductionRateToServer,
      ]
    );

    // Memoized helper functions for styling using our new type
    const getFieldBackground = useCallback(
      (item: CostDisplayProperties) => {
        // Check for the different states in order of precedence
        if (item.isquoted) {
          // Quoted items - use neutral gray
          return darkMode
            ? alpha(muiTheme.palette.grey[700], 0.7)
            : alpha(muiTheme.palette.grey[300], 0.9);
        }

        if (item.isoverrideitemunitcost || item.isoverridehourlyproductionrate) {
          // Override values - use warning color (yellow-ish)
          return darkMode
            ? alpha(muiTheme.palette.warning.dark, 0.2)
            : alpha(muiTheme.palette.warning.light, 0.2);
        }

        if (
          (item.itemunitcost === 0 && 'itemunitcost' in item) ||
          (item.hourlyproductionrate === 0 && 'hourlyproductionrate' in item)
        ) {
          // Zero values - use error color (red-ish)
          return darkMode
            ? alpha(muiTheme.palette.error.dark, 0.15)
            : alpha(muiTheme.palette.error.light, 0.15);
        }

        // Default values - use success color (green-ish)
        return darkMode
          ? alpha(muiTheme.palette.success.dark, 0.15)
          : alpha(muiTheme.palette.success.light, 0.15);
      },
      [muiTheme, darkMode]
    );

    const getFieldTextColor = useCallback(
      (item: CostDisplayProperties) => {
        // Check for the different states in order of precedence
        if (item.isquoted) {
          // Quoted items - use muted text
          return darkMode
            ? alpha(muiTheme.palette.text.secondary, 0.9)
            : muiTheme.palette.text.secondary;
        }

        if (item.isoverrideitemunitcost || item.isoverridehourlyproductionrate) {
          // Override values - use warning color
          return darkMode ? muiTheme.palette.warning.light : muiTheme.palette.warning.dark;
        }

        if (
          (item.itemunitcost === 0 && 'itemunitcost' in item) ||
          (item.hourlyproductionrate === 0 && 'hourlyproductionrate' in item)
        ) {
          // Zero values - use error color
          return darkMode ? muiTheme.palette.error.light : muiTheme.palette.error.dark;
        }

        // Default values - use success color
        return darkMode ? muiTheme.palette.success.light : muiTheme.palette.success.dark;
      },
      [muiTheme, darkMode]
    );

    // Status icons - memoized
    const unitCostStatusIcon = useMemo(() => {
      // Use localItem for optimistic updates
      if (localItem.isoverrideitemunitcost) {
        return (
          <Tooltip title="Custom override value" arrow>
            <EditNoteIcon
              fontSize="small"
              sx={{
                color: muiTheme.palette.primary.main,
                ml: 0.5,
              }}
            />
          </Tooltip>
        );
      } else if (!localItem.itemunitcost || localItem.itemunitcost === 0) {
        if (localItem.isquoted) {
          return (
            <Tooltip title="Vendor quote expected" arrow>
              <RequestQuoteIcon
                fontSize="small"
                sx={{
                  color: muiTheme.palette.warning.main,
                  ml: 0.5,
                }}
              />
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title="Zero cost value" arrow>
              <ErrorOutlineIcon
                fontSize="small"
                sx={{
                  color: muiTheme.palette.warning.main,
                  ml: 0.5,
                }}
              />
            </Tooltip>
          );
        }
      } else {
        // Return null for default values - green text color is sufficient indicator
        return null;
      }
    }, [localItem.isoverrideitemunitcost, localItem.itemunitcost, muiTheme, localItem.isquoted]);

    const productionRateStatusIcon = useMemo(() => {
      // Use localItem for optimistic updates
      if (localItem.isoverridehourlyproductionrate) {
        return (
          <Tooltip title="Custom override value" arrow>
            <EditNoteIcon
              fontSize="small"
              sx={{
                color: muiTheme.palette.primary.main,
                ml: 0.5,
              }}
            />
          </Tooltip>
        );
      } else if (!localItem.hourlyproductionrate || localItem.hourlyproductionrate === 0) {
        return (
          <Tooltip title="Zero production rate" arrow>
            <ErrorOutlineIcon
              fontSize="small"
              sx={{
                color: muiTheme.palette.warning.main,
                ml: 0.5,
              }}
            />
          </Tooltip>
        );
      } else {
        // Return null for default values - green text color is sufficient indicator
        return null;
      }
    }, [localItem.isoverridehourlyproductionrate, localItem.hourlyproductionrate, muiTheme]);

    // Replace unitCostStatusIcon with a combined approach that shows both icons when needed
    const combinedUnitCostIcons = useMemo(() => {
      return (
        <>
          {unitCostStatusIcon}
          {lastUpdatedWarningIcon}
        </>
      );
    }, [unitCostStatusIcon, lastUpdatedWarningIcon]);

    // Helper function to determine if a cell should be highlighted based on selected column
    const getColumnHighlightStyle = useCallback(
      (columnIndex: number) => {
        if (selectedColumnIndex === columnIndex) {
          return {
            backgroundColor: alpha(muiTheme.palette.primary.main, darkMode ? 0.2 : 0.1),
            borderLeft: `2px solid ${muiTheme.palette.primary.main}`,
            borderRight: `2px solid ${muiTheme.palette.primary.main}`,
          };
        }
        return {};
      },
      [selectedColumnIndex, muiTheme, darkMode]
    );

    // Make handleCellClick simpler to avoid interfering with tab navigation
    const handleCellClick = useCallback(
      (columnIndex: number) => (e: React.MouseEvent) => {
        // First handle shift+click for column-based selection
        if (isShiftSelecting && onSelect) {
          // If shift is held and we're clicking a cell, select the row and notify about column
          onSelect(item.itemid, !isSelected, true, columnIndex);
          e.stopPropagation(); // Prevent the event from bubbling
          return;
        }

        // We'll let native focus handling work for input fields
        // Instead of programmatically trying to focus specific cells
      },
      [isShiftSelecting, onSelect, item.itemid, isSelected]
    );

    // More optimistic updates for unit cost field
    const handleUnitCostChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '') {
          setUnitCostInput('');
          // Also update local item for immediate UI feedback even during typing
          setLocalItem(prev => ({
            ...prev,
            totalcost: 0,
          }));
        } else if (value.match(/^[0-9]*\.?[0-9]{0,2}$/)) {
          setUnitCostInput(value);
          // Update total cost optimistically while typing
          const numericValue = Number(value) || 0;
          setLocalItem(prev => {
            // Calculate total cost with UOM adjustment
            const totalCost = calculateTotalMaterialCost(
              numericValue,
              prev.totalitemqty,
              prev.itemuomvalue
            );
            return {
              ...prev,
              totalcost: totalCost,
            };
          });
        }
      },
      [setLocalItem]
    );

    // More optimistic updates for production rate field
    const handleProductionRateChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '') {
          setProductionRateInput('');
          // Also update local item for immediate UI feedback
          setLocalItem(prev => ({
            ...prev,
            totaldirectlaborhours: 0,
          }));
        } else if (value.match(/^[0-9]*\.?[0-9]{0,3}$/)) {
          setProductionRateInput(value);
          // Update labor hours optimistically while typing
          const numericValue = Number(value) || 0;
          setLocalItem(prev => {
            // Calculate labor hours with UOM adjustment
            const laborHours = calculateTotalLaborHours(
              numericValue,
              prev.totalitemqty,
              prev.itemuomvalue
            );
            return {
              ...prev,
              totaldirectlaborhours: laborHours,
            };
          });
        }
      },
      [setLocalItem]
    );

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
          padding: '8px',
          textOverflow: 'ellipsis',
          transition: 'background-color 0.2s, outline 0.2s',
          backgroundColor: isSelected
            ? alpha(muiTheme.palette.primary.main, darkMode ? 0.08 : 0.04)
            : isActiveRow
              ? alpha(muiTheme.palette.primary.main, darkMode ? 0.1 : 0.05)
              : 'transparent',
          '&:hover': {
            backgroundColor: isSelected
              ? alpha(muiTheme.palette.primary.main, darkMode ? 0.12 : 0.06)
              : alpha(muiTheme.palette.primary.main, darkMode ? 0.1 : 0.05),
          },
          '&:nth-of-type(even)': {
            backgroundColor: isSelected
              ? alpha(muiTheme.palette.primary.main, darkMode ? 0.08 : 0.04)
              : isActiveRow
                ? alpha(muiTheme.palette.primary.main, darkMode ? 0.1 : 0.05)
                : alpha(
                    darkMode ? muiTheme.palette.common.white : muiTheme.palette.common.black,
                    darkMode ? 0.02 : 0.02
                  ),
          },
          position: 'relative',
          outline: isSelected
            ? `1px solid ${alpha(muiTheme.palette.primary.main, 0.5)}`
            : isActiveRow
              ? `1px solid ${alpha(muiTheme.palette.primary.main, 0.7)}`
              : 'none',
        }}
        role="row"
        tabIndex={0}
        onKeyDown={e => {
          // Handle row-level keyboard navigation
          if (e.key === 'Enter') {
            // Only select the checkbox if we're not currently editing a field
            const isInputActive = document.activeElement instanceof HTMLInputElement;
            if (!isInputActive) {
              if (onSelect) {
                onSelect(item.itemid, !isSelected);
              } else {
                unitCostInputRef.current?.focus();
              }
            }
          }
        }}
      >
        {/* Name - 18% */}
        <Box
          sx={{
            width: '18%',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: muiTheme.palette.text.primary,
            px: 1,
            ...getColumnHighlightStyle(1),
          }}
          title={localItem.itemname || 'N/A'}
          role="cell"
          onClick={handleCellClick(1)}
        >
          <Typography variant="body2">{localItem.itemname || 'N/A'}</Typography>
        </Box>

        {/* Attribute Group - 10% */}
        <Box
          sx={{
            width: '10%',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: muiTheme.palette.text.primary,
            px: 1,
            ...getColumnHighlightStyle(2),
          }}
          title={localItem.attributegroupname || 'N/A'}
          role="cell"
          onClick={handleCellClick(2)}
        >
          <Typography variant="body2">{localItem.attributegroupname || 'N/A'}</Typography>
        </Box>

        {/* Item Qty - 8% */}
        <Box
          sx={{
            width: '8%',
            textAlign: 'center',
            color: muiTheme.palette.text.primary,
            ...getColumnHighlightStyle(3),
          }}
          role="cell"
          onClick={handleCellClick(3)}
        >
          <Typography variant="body2">{localItem.totalitemqty.toFixed(3) || 0}</Typography>
        </Box>

        {/* Item UOM - 8% */}
        <Box
          sx={{
            width: '8%',
            textAlign: 'center',
            color: muiTheme.palette.text.primary,
            ...getColumnHighlightStyle(4),
          }}
          role="cell"
          onClick={handleCellClick(4)}
        >
          <Typography variant="body2">{localItem.itemuomname || 'N/A'}</Typography>
        </Box>

        {/* Item Cost Per UOM - 12% */}
        <Box
          sx={{
            width: '12%',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            ...getColumnHighlightStyle(5),
          }}
          role="cell"
          onClick={handleCellClick(5)}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <TextField
              type="text"
              size="small"
              value={unitCostInput}
              onChange={handleUnitCostChange}
              onFocus={() => !isEditingDisabled && handleFocus('unitCost')}
              onBlur={() => {
                setIsEditingUnitCost(false);
                // Always commit the value on blur
                if (!isEditingDisabled) {
                  commitUnitCostToServer(unitCostInput);
                }
              }}
              onKeyDown={e => handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, 'unitCost')}
              inputRef={unitCostInputRef}
              inputProps={{
                min: 0,
                max: 99999999.99,
                step: '0.01',
                'aria-label': 'Unit cost override',
                tabIndex: 0, // Ensure tab navigation works
              }}
              InputProps={{
                style: sxUnitCostOverrideValue(localItem),
                sx: {
                  bgcolor: getFieldBackground({
                    isoverrideitemunitcost: localItem.isoverrideitemunitcost,
                    itemunitcost: localItem.itemunitcost,
                    isquoted: localItem.isquoted,
                  }),
                  '& .MuiInputBase-input': {
                    color: getFieldTextColor({
                      isoverrideitemunitcost: localItem.isoverrideitemunitcost,
                      itemunitcost: localItem.itemunitcost,
                      isquoted: localItem.isquoted,
                    }),
                    textAlign: 'center',
                    fontWeight: localItem.isoverrideitemunitcost ? 600 : 400,
                    transition: 'all 0.2s',
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 2px ${muiTheme.palette.primary.main}`,
                    bgcolor: darkMode
                      ? alpha(muiTheme.palette.background.default, 0.2)
                      : alpha(
                          getFieldBackground({
                            isoverrideitemunitcost: localItem.isoverrideitemunitcost,
                            itemunitcost: localItem.itemunitcost,
                            isquoted: localItem.isquoted,
                          }),
                          0.8
                        ),
                  },
                  '&.Mui-disabled': {
                    opacity: 0.7,
                    bgcolor: alpha(muiTheme.palette.background.paper, 0.3),
                    '& .MuiInputBase-input': {
                      color: alpha(muiTheme.palette.text.disabled, 0.7),
                    },
                  },
                  borderRadius: 1,
                  border: `1px solid ${alpha(
                    getFieldTextColor({
                      isoverrideitemunitcost: localItem.isoverrideitemunitcost,
                      itemunitcost: localItem.itemunitcost,
                      isquoted: localItem.isquoted,
                    }),
                    0.4
                  )}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: darkMode
                      ? alpha(muiTheme.palette.background.default, 0.3)
                      : alpha(
                          getFieldBackground({
                            isoverrideitemunitcost: localItem.isoverrideitemunitcost,
                            itemunitcost: localItem.itemunitcost,
                            isquoted: localItem.isquoted,
                          }),
                          0.7
                        ),
                    borderColor: alpha(
                      getFieldTextColor({
                        isoverrideitemunitcost: localItem.isoverrideitemunitcost,
                        itemunitcost: localItem.itemunitcost,
                        isquoted: localItem.isquoted,
                      }),
                      0.6
                    ),
                  },
                },
              }}
              disabled={localItem.isquoted || isEditingDisabled || lockBid()}
              sx={{ width: '85px', flexShrink: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {combinedUnitCostIcons}
            </Box>
          </Box>
        </Box>

        {/* Total Material Cost - 12% */}
        <Box
          sx={{
            width: '12%',
            textAlign: 'center',
            color: muiTheme.palette.text.primary,
            ...getColumnHighlightStyle(6),
          }}
          role="cell"
          onClick={handleCellClick(6)}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              color: muiTheme.palette.success.main,
            }}
          >
            {/* Calculate total cost based on current state */}
            {formatCurrency(
              isEditingUnitCost
                ? calculateTotalMaterialCost(
                    Number(unitCostInput || 0),
                    localItem.totalitemqty,
                    localItem.itemuomvalue
                  )
                : localItem.totalcost
            ) || 0}
          </Typography>
        </Box>

        {/* Hours Per Unit - 12% */}
        <Box
          sx={{
            width: '12%',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            ...getColumnHighlightStyle(7),
          }}
          role="cell"
          onClick={handleCellClick(7)}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <TextField
              type="text"
              size="small"
              value={productionRateInput}
              onChange={handleProductionRateChange}
              onFocus={() => !isEditingDisabled && handleFocus('productionRate')}
              onBlur={() => {
                setIsEditingProductionRate(false);
                // Always commit the value on blur
                if (!isEditingDisabled) {
                  commitProductionRateToServer(productionRateInput);
                }
              }}
              onKeyDown={e =>
                handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, 'productionRate')
              }
              inputRef={productionRateInputRef}
              inputProps={{
                min: 0,
                max: 9999.999,
                step: '0.001',
                'aria-label': 'Production rate override',
                tabIndex: 0, // Ensure tab navigation works
              }}
              InputProps={{
                style: sxProductionCostOverrideValue(localItem),
                sx: {
                  bgcolor: getFieldBackground({
                    isoverridehourlyproductionrate: localItem.isoverridehourlyproductionrate,
                    hourlyproductionrate: localItem.hourlyproductionrate,
                    isquoted: localItem.isquoted,
                  }),
                  '& .MuiInputBase-input': {
                    color: getFieldTextColor({
                      isoverridehourlyproductionrate: localItem.isoverridehourlyproductionrate,
                      hourlyproductionrate: localItem.hourlyproductionrate,
                      isquoted: localItem.isquoted,
                    }),
                    textAlign: 'center',
                    fontWeight: localItem.isoverridehourlyproductionrate ? 600 : 400,
                    transition: 'all 0.2s',
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 2px ${muiTheme.palette.primary.main}`,
                    bgcolor: darkMode
                      ? alpha(muiTheme.palette.background.default, 0.2)
                      : alpha(
                          getFieldBackground({
                            isoverridehourlyproductionrate:
                              localItem.isoverridehourlyproductionrate,
                            hourlyproductionrate: localItem.hourlyproductionrate,
                            isquoted: localItem.isquoted,
                          }),
                          0.8
                        ),
                  },
                  '&.Mui-disabled': {
                    opacity: 0.7,
                    bgcolor: alpha(muiTheme.palette.background.paper, 0.3),
                    '& .MuiInputBase-input': {
                      color: alpha(muiTheme.palette.text.disabled, 0.7),
                    },
                  },
                  borderRadius: 1,
                  border: `1px solid ${alpha(
                    getFieldTextColor({
                      isoverridehourlyproductionrate: localItem.isoverridehourlyproductionrate,
                      hourlyproductionrate: localItem.hourlyproductionrate,
                      isquoted: localItem.isquoted,
                    }),
                    0.4
                  )}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: darkMode
                      ? alpha(muiTheme.palette.background.default, 0.3)
                      : alpha(
                          getFieldBackground({
                            isoverridehourlyproductionrate:
                              localItem.isoverridehourlyproductionrate,
                            hourlyproductionrate: localItem.hourlyproductionrate,
                            isquoted: localItem.isquoted,
                          }),
                          0.7
                        ),
                    borderColor: alpha(
                      getFieldTextColor({
                        isoverridehourlyproductionrate: localItem.isoverridehourlyproductionrate,
                        hourlyproductionrate: localItem.hourlyproductionrate,
                        isquoted: localItem.isquoted,
                      }),
                      0.6
                    ),
                  },
                },
              }}
              disabled={isEditingDisabled || lockBid()}
              sx={{ width: '85px', flexShrink: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {productionRateStatusIcon}
            </Box>
          </Box>
        </Box>

        {/* Total Labor Hours - 8% */}
        <Box
          sx={{
            width: '8%',
            textAlign: 'center',
            color: muiTheme.palette.text.primary,
            ...getColumnHighlightStyle(8),
          }}
          role="cell"
          onClick={handleCellClick(8)}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                color: muiTheme.palette.info.main,
              }}
            >
              {/* Calculate labor hours based on current input */}
              {formatNumber(
                isEditingProductionRate && Number(productionRateInput) > 0
                  ? calculateTotalLaborHours(
                      Number(productionRateInput),
                      localItem.totalitemqty,
                      localItem.itemuomvalue
                    )
                  : localItem.totaldirectlaborhours
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: muiTheme.palette.text.secondary,
                fontSize: '0.7rem',
                lineHeight: 1,
              }}
            >
              {getMinutesFromDecimal(
                isEditingProductionRate && Number(productionRateInput) > 0
                  ? calculateTotalLaborHours(
                      Number(productionRateInput),
                      localItem.totalitemqty,
                      localItem.itemuomvalue
                    )
                  : localItem.totaldirectlaborhours
              )}
            </Typography>
          </Box>
        </Box>

        {/* Actions - 12% */}
        <Box
          sx={{
            width: '12%',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            ...getColumnHighlightStyle(9),
          }}
          role="cell"
          onClick={handleCellClick(9)}
        >
          {localItem.attributegroupname !== 'System' && (
            <Tooltip
              title={
                localItem.attributegroupname === 'System'
                  ? 'Direct Editing of Community Items is not allowed'
                  : 'Edit User Item Costs'
              }
              arrow
            >
              <span>
                <Button
                  onClick={() => onOpenCreateItemTab(localItem.itemid)}
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  aria-label={`Edit ${localItem.itemname || 'item'}`}
                  disabled={
                    localItem.attributegroupid === '310' ||
                    localItem.assemblyname === 'System' ||
                    localItem.attributevaluename === 'Foundry Item'
                  } //IMPORTANT: We dont let users edit system items as this will effect all pother users
                  sx={{
                    minWidth: 0,
                    px: { xs: 1, sm: 2 },
                    '& .MuiButton-startIcon': {
                      mr: { xs: 0, sm: 1 },
                    },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': {
                      fontSize: 18,
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Edit
                  </Box>
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={
              localItem.isoverrideitemunitcost || localItem.isoverridehourlyproductionrate
                ? 'Reset to default values'
                : 'No overrides to reset'
            }
            arrow
          >
            <span>
              <Button
                onClick={resetOverrides}
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<RestartAltIcon fontSize="small" />}
                aria-label={`Reset overrides for ${localItem.itemname || 'item'}`}
                disabled={
                  (!localItem.isoverrideitemunitcost &&
                    !localItem.isoverridehourlyproductionrate) ||
                  (isBatchEditing && !isSelected) ||
                  isEditingDisabled ||
                  lockBid()
                }
                sx={{
                  minWidth: 0,
                  px: { xs: 1, sm: 2 },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 1 },
                  },
                  '& .MuiButton-startIcon > *:nth-of-type(1)': {
                    fontSize: 18,
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Reset
                </Box>
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  },
  // Optimize memo comparison to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // Only re-render if these critical props change
    if (prevProps.isSelected !== nextProps.isSelected) return false;
    if (prevProps.isActiveRow !== nextProps.isActiveRow) return false;
    if (prevProps.isEditingDisabled !== nextProps.isEditingDisabled) return false;
    if (prevProps.item.itemid !== nextProps.item.itemid) return false;

    // For data changes, only re-render if the relevant fields changed
    if (prevProps.item.totalitemqty !== nextProps.item.totalitemqty) return false;
    if (prevProps.item.itemunitcost !== nextProps.item.itemunitcost) return false;
    if (prevProps.item.hourlyproductionrate !== nextProps.item.hourlyproductionrate) return false;
    if (prevProps.item.isoverrideitemunitcost !== nextProps.item.isoverrideitemunitcost)
      return false;
    if (
      prevProps.item.isoverridehourlyproductionrate !==
      nextProps.item.isoverridehourlyproductionrate
    )
      return false;

    // Skip re-render if props are essentially the same
    return true;
  }
);
